import React, { useEffect, useState } from 'react';
import {
    Box,
    Container,
    Typography,
    Paper,
    Card,
    CardContent,
    CardActions,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Link,
    Stack,
    Chip,
    MenuItem
} from '@mui/material';
import {
    DndContext,
    DragOverlay,
    closestCorners,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
    useDroppable
} from '@dnd-kit/core';
import {
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
    Email as EmailIcon,
    LinkedIn as LinkedInIcon,
    Phone as PhoneIcon,
    AttachMoney as MoneyIcon,
    CalendarToday as CalendarIcon,
    Description as DescriptionIcon,
    People as PeopleIcon
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { jobVacancyService } from '../services/jobVacancyService';
import { JobVacancy, JobApplication, RecruitmentStep, SENIORITY_LABELS } from '../types/jobVacancy';

// Definindo os steps como strings literais para garantir consistência
const RECRUITMENT_STEPS = [
    'RESUME_SCREENING',
    'BEHAVIORAL_EVALUATION',
    'TECHNICAL_TEST',
    'RESUME_AND_TEST_EVALUATION',
    'TECHNICAL_INTERVIEW',
    'NEGOTIATION',
    'CLOSED',
    'REJECTED'
] as const;

type RecruitmentStepLiteral = typeof RECRUITMENT_STEPS[number];

// Função auxiliar para converter string literal para enum
const toRecruitmentStep = (step: RecruitmentStepLiteral): RecruitmentStep => {
    return RecruitmentStep[step as keyof typeof RecruitmentStep];
};

// Função auxiliar para converter enum para string literal
const fromRecruitmentStep = (step: RecruitmentStep): RecruitmentStepLiteral => {
    return RecruitmentStep[step] as RecruitmentStepLiteral;
};

const STEP_TITLES: Record<RecruitmentStepLiteral, string> = {
    'RESUME_SCREENING': 'Triagem de Currículo',
    'BEHAVIORAL_EVALUATION': 'Avaliação Comportamental',
    'TECHNICAL_TEST': 'Teste Técnico',
    'RESUME_AND_TEST_EVALUATION': 'Avaliação de Currículo e Teste',
    'TECHNICAL_INTERVIEW': 'Entrevista Técnica',
    'NEGOTIATION': 'Negociação',
    'CLOSED': 'Fechado',
    'REJECTED': 'Rejeitado'
};

// Tipos
interface FeedbackDialogProps {
    open: boolean;
    application: JobApplication | null;
    onClose: () => void;
    onSave: (feedback: string, technicalFeedback?: string) => void;
}

interface ApplicationCardProps {
    application: JobApplication;
    onFeedbackClick: () => void;
    onMove: (step: RecruitmentStepLiteral) => void;
}

interface DroppableColumnProps {
    step: RecruitmentStepLiteral;
    title: string;
    applications: JobApplication[];
    onFeedbackClick: (app: JobApplication) => void;
    onMove: (app: JobApplication, step: RecruitmentStepLiteral) => void;
}

// Componente do Card de Aplicação
const ApplicationCard: React.FC<ApplicationCardProps> = ({ application, onFeedbackClick, onMove }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging
    } = useSortable({
        id: application.id.toString()
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1
    };

    const formatDate = (date: string) => {
        return new Date(date).toLocaleDateString('pt-BR');
    };

    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(value);
    };

    return (
        <Card
            ref={setNodeRef}
            style={style}
            {...attributes}
            {...listeners}
            sx={{ mb: 2, cursor: 'grab' }}
        >
            <CardContent>
                <Typography variant="h6" gutterBottom>
                    {application.candidate_name}
                </Typography>
                <Stack spacing={1}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <EmailIcon fontSize="small" />
                        <Link href={`mailto:${application.candidate_email}`}>
                            {application.candidate_email}
                        </Link>
                    </Box>

                    {application.phone && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PhoneIcon fontSize="small" />
                            <Typography>{application.phone}</Typography>
                        </Box>
                    )}

                    {application.linkedin_url && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LinkedInIcon fontSize="small" />
                            <Link href={application.linkedin_url} target="_blank">
                                LinkedIn
                            </Link>
                        </Box>
                    )}

                    {application.salary_expectation && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <MoneyIcon fontSize="small" />
                            <Typography>
                                {formatCurrency(application.salary_expectation)}
                            </Typography>
                        </Box>
                    )}

                    {application.availability_date && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CalendarIcon fontSize="small" />
                            <Typography>
                                Disponível a partir de {formatDate(application.availability_date)}
                            </Typography>
                        </Box>
                    )}

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <DescriptionIcon fontSize="small" />
                        <Link component="button" type="button" onClick={() => jobVacancyService.openResume(application.resume_url).catch(() => alert('Não foi possível abrir o currículo'))}>
                            Currículo
                        </Link>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PeopleIcon fontSize="small" />
                        <Link href={`/talent-pool?candidate=${application.candidate_id}`}>
                            Ver no Banco de Talentos
                        </Link>
                    </Box>

                    {application.other_applications_count > 0 && (
                        <Box sx={{ mt: 1 }}>
                            <Chip
                                size="small"
                                color="info"
                                label={`Em ${application.other_applications_count} outro${application.other_applications_count > 1 ? 's' : ''} processo${application.other_applications_count > 1 ? 's' : ''}`}
                            />
                        </Box>
                    )}
                </Stack>
            </CardContent>
            <CardActions sx={{ flexWrap: 'wrap', gap: 1 }}>
                <Button size="small" onClick={onFeedbackClick}>
                    Feedback
                </Button>
                {/* Alternativa ao arrastar (inviável no celular e por teclado) */}
                <TextField
                    select
                    size="small"
                    label="Etapa"
                    value={fromRecruitmentStep(application.current_step)}
                    onChange={(e) => onMove(e.target.value as RecruitmentStepLiteral)}
                    onPointerDown={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    sx={{ minWidth: 180, flex: 1 }}
                >
                    {RECRUITMENT_STEPS.map((step) => (
                        <MenuItem key={step} value={step}>{STEP_TITLES[step]}</MenuItem>
                    ))}
                </TextField>
            </CardActions>
        </Card>
    );
};

// Componente da Coluna Droppable
const DroppableColumn: React.FC<DroppableColumnProps> = ({ step, title, applications, onFeedbackClick, onMove }) => {
    const { setNodeRef } = useDroppable({
        id: step
    });

    return (
        <Box sx={{ width: { xs: 260, sm: 300 }, minWidth: { xs: 260, sm: 300 } }}>
            <Paper 
                ref={setNodeRef}
                sx={{ p: 2, height: '100%', bgcolor: 'grey.100' }}
            >
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    {title}
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {applications.length}
                    </Typography>
                </Typography>

                <Box
                    sx={{
                        minHeight: 100,
                        p: 1,
                        bgcolor: 'background.paper',
                        borderRadius: 1
                    }}
                >
                    <SortableContext
                        items={applications.map(app => app.id.toString())}
                        strategy={verticalListSortingStrategy}
                    >
                        {applications.map((app) => (
                            <ApplicationCard
                                key={app.id}
                                application={app}
                                onFeedbackClick={() => onFeedbackClick(app)}
                                onMove={(newStep) => onMove(app, newStep)}
                            />
                        ))}
                    </SortableContext>
                </Box>
            </Paper>
        </Box>
    );
};

// Componente do Dialog de Feedback
const FeedbackDialog: React.FC<FeedbackDialogProps> = ({ open, application, onClose, onSave }) => {
    const [feedback, setFeedback] = useState(application?.feedback || '');
    const [technicalFeedback, setTechnicalFeedback] = useState(application?.technical_feedback || '');

    useEffect(() => {
        if (application) {
            setFeedback(application.feedback || '');
            setTechnicalFeedback(application.technical_feedback || '');
        }
    }, [application]);

    const handleSave = () => {
        onSave(feedback, technicalFeedback);
        onClose();
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Feedback do Candidato</DialogTitle>
            <DialogContent>
                <Box sx={{ mt: 2 }}>
                    <TextField
                        fullWidth
                        label="Feedback Geral"
                        multiline
                        rows={4}
                        value={feedback}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFeedback(e.target.value)}
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        fullWidth
                        label="Feedback Técnico"
                        multiline
                        rows={4}
                        value={technicalFeedback}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTechnicalFeedback(e.target.value)}
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancelar</Button>
                <Button onClick={handleSave} variant="contained">
                    Salvar
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// Componente Principal
export const VacancyApplications: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const { enqueueSnackbar } = useSnackbar();
    const [vacancy, setVacancy] = useState<JobVacancy | null>(null);
    const [selectedApplication, setSelectedApplication] = useState<JobApplication | null>(null);
    const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
    const [columns, setColumns] = useState<Record<RecruitmentStepLiteral, JobApplication[]>>(() => {
        const initialColumns: Record<RecruitmentStepLiteral, JobApplication[]> = {} as Record<RecruitmentStepLiteral, JobApplication[]>;
        RECRUITMENT_STEPS.forEach(step => {
            initialColumns[step] = [];
        });
        return initialColumns;
    });

    const sensors = useSensors(
        // Sem distância mínima, qualquer toque no card virava arraste e engolia os cliques
        useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    useEffect(() => {
        if (id) {
            loadVacancy();
        }
    }, [id]);

    const loadVacancy = async () => {
        try {
            const data = await jobVacancyService.getVacancy(Number(id));
            setVacancy(data);

            const newColumns: Record<RecruitmentStepLiteral, JobApplication[]> = {} as Record<RecruitmentStepLiteral, JobApplication[]>;
            RECRUITMENT_STEPS.forEach(step => {
                newColumns[step] = [];
            });

            data.applications.forEach(app => {
                if (app.current_step) {
                    const stepKey = fromRecruitmentStep(app.current_step);
                    if (RECRUITMENT_STEPS.includes(stepKey)) {
                        newColumns[stepKey].push(app);
                    }
                }
            });

            setColumns(newColumns);
        } catch (error) {
            enqueueSnackbar('Erro ao carregar candidaturas', { variant: 'error' });
        }
    };

    const stepOf = (applicationId: number) => Object.keys(columns).find(step =>
        columns[step as RecruitmentStepLiteral].some(app => app.id === applicationId)
    ) as RecruitmentStepLiteral | undefined;

    const handleDragEnd = async (event: DragEndEvent) => {
        const { active, over } = event;

        if (!over || active.id === over.id) return;

        const applicationId = Number(active.id);
        // Soltar sobre outro card: o destino é a coluna desse card
        const overId = String(over.id);
        const newStep = (RECRUITMENT_STEPS as readonly string[]).includes(overId)
            ? overId as RecruitmentStepLiteral
            : stepOf(Number(overId));
        if (newStep) await moveApplication(applicationId, newStep);
    };

    const moveApplication = async (applicationId: number, newStep: RecruitmentStepLiteral) => {
        const sourceStep = stepOf(applicationId);

        if (!sourceStep || !newStep || sourceStep === newStep) return;

        try {
            const newColumns = { ...columns };
            const sourceList = [...newColumns[sourceStep]];
            const destList = [...newColumns[newStep]];

            const applicationIndex = sourceList.findIndex(app => app.id === applicationId);
            if (applicationIndex === -1) return;

            const [original] = sourceList.splice(applicationIndex, 1);
            const movedItem = { ...original, current_step: toRecruitmentStep(newStep) };
            destList.push(movedItem);

            newColumns[sourceStep] = sourceList;
            newColumns[newStep] = destList;
            setColumns(newColumns);

            await jobVacancyService.updateApplicationStep(Number(id), applicationId, {
                step: toRecruitmentStep(newStep),
                feedback: movedItem.feedback,
                technical_feedback: movedItem.technical_feedback
            });

            enqueueSnackbar('Candidatura atualizada com sucesso', { variant: 'success' });
        } catch (error) {
            loadVacancy();
            enqueueSnackbar('Erro ao atualizar candidatura', { variant: 'error' });
        }
    };

    const handleFeedbackSave = async (feedback: string, technicalFeedback?: string) => {
        if (!selectedApplication) return;

        try {
            await jobVacancyService.updateApplicationStep(
                Number(id),
                selectedApplication.id,
                {
                    step: selectedApplication.current_step,
                    feedback,
                    technical_feedback: technicalFeedback
                }
            );
            loadVacancy();
            enqueueSnackbar('Feedback salvo com sucesso', { variant: 'success' });
        } catch (error) {
            enqueueSnackbar('Erro ao salvar feedback', { variant: 'error' });
        }
    };

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCorners}
            onDragEnd={handleDragEnd}
        >
            <Container maxWidth="xl">
                <Box sx={{ py: 4 }}>
                    {vacancy && (
                        <Typography variant="h4" component="h1" gutterBottom sx={{ fontSize: { xs: '1.75rem', md: '2.125rem' } }}>
                            Candidaturas - {vacancy.role}
                            {vacancy.seniority ? ` (${SENIORITY_LABELS[vacancy.seniority] || vacancy.seniority})` : ''}
                        </Typography>
                    )}

                    <Box
                        sx={{
                            display: 'flex',
                            overflowX: 'auto',
                            gap: 2,
                            py: 2,
                            minHeight: 'calc(100vh - 200px)'
                        }}
                    >
                        {RECRUITMENT_STEPS.map((step) => (
                            <DroppableColumn
                                key={step}
                                step={step}
                                title={STEP_TITLES[step]}
                                applications={columns[step]}
                                onFeedbackClick={(app: JobApplication) => {
                                    setSelectedApplication(app);
                                    setFeedbackDialogOpen(true);
                                }}
                                onMove={(app, newStep) => moveApplication(app.id, newStep)}
                            />
                        ))}
                    </Box>
                </Box>

                <FeedbackDialog
                    open={feedbackDialogOpen}
                    application={selectedApplication}
                    onClose={() => {
                        setFeedbackDialogOpen(false);
                        setSelectedApplication(null);
                    }}
                    onSave={handleFeedbackSave}
                />
            </Container>
        </DndContext>
    );
}; 
